import {
  CollatableEntityCollections,
  CollatableEntityCollectionsRepository,
  defaultEntityCollation,
  EntityCollation
} from '../root-store-common'
import {
  DataAction,
  Payload,
  StateRepository
} from '@angular-ru/ngxs/decorators'
import { Selector, State } from '@ngxs/store'
import {
  createEntityCollections,
  EntityDictionary
} from '@angular-ru/cdk/entity'
import { inject, Injectable } from '@angular/core'
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  EMPTY,
  from,
  Observable,
  of,
  reduce,
  tap
} from 'rxjs'
import {
  InsightDTO,
  InsightsAllActiveTotalCount,
  InsightsBackend
} from '../../shared/model/insight.model'
import { BackendService } from '../../shared/services/backend.service'
import { PatientState } from '../patient/patient.state'
import { StoreEventsService } from '../store-events.service'
import { DepartmentDTO } from '../../shared/model/permission.model'
import { DepartmentState } from '../department/department.state'
import { orderBy } from 'lodash-es'
import { entitiesFilter } from '../../core/helpers/filter'
import { catchError } from 'rxjs/operators'
import { BffPatient } from '../../shared/model/bff/patient'
import { ErrorState } from '../error/error.state'

export const insightFeatureName = 'residentsInsights'

interface ResidentInsightStateCollation extends EntityCollation {
  hasAllLoaded: boolean
  isInitialLoading: boolean
  freeTextFilter: string
}

@StateRepository()
@State<CollatableEntityCollections<InsightDTO, ResidentInsightStateCollation>>({
  name: insightFeatureName,
  defaults: {
    ...createEntityCollections(),
    ...defaultEntityCollation(),
    hasAllLoaded: false,
    isInitialLoading: true,
    freeTextFilter: ''
  }
})
@Injectable()
export class ResidentInsightsState extends CollatableEntityCollectionsRepository<
  InsightDTO,
  ResidentInsightStateCollation
> {
  private readonly backendService = inject(BackendService)
  private readonly patientState = inject(PatientState)
  private readonly errorState = inject(ErrorState)
  private readonly storeEvents = inject(StoreEventsService)
  private readonly departmentState = inject(DepartmentState)

  private insightsAllActiveTotalCount$ =
    new BehaviorSubject<InsightsAllActiveTotalCount>({
      activeTotalCount: 0,
      allTotalCount: 0
    })
  public readonly insightsAllActiveTotalCountObs$ =
    this.insightsAllActiveTotalCount$.asObservable()

  constructor() {
    super()
  }

  @Selector()
  public static isLoading(
    state: CollatableEntityCollections<InsightDTO>
  ): boolean {
    return state.isLoading
  }

  @Selector([
    ResidentInsightsState,
    PatientState.entities,
    DepartmentState.currentDepartments
  ])
  public static insights(
    state: CollatableEntityCollections<InsightDTO>,
    patient: EntityDictionary<string, BffPatient>,
    currentDepartments: DepartmentDTO[] | null
  ): InsightDTO[] {
    let patients = Object.values(patient)
    if (currentDepartments) {
      patients = patients.filter(
        patient =>
          patient.department &&
          currentDepartments
            .map(department => department.id)
            .includes(patient.department.id)
      )
    }
    const currentInsights: any[] = []
    Object.values(state.entities).forEach(insight => {
      if (insight.patient && insight.patient.id) {
        const patient: BffPatient | undefined = Object.values(patients).find(
          p => p.id === insight.patient?.id
        )
        if (patient) {
          currentInsights.push({
            ...insight,
            patient: {
              ...patient,
              avatar: patient.avatar
            }
          })
        }
      }
    })
    return currentInsights
  }

  @Selector([
    ResidentInsightsState,
    PatientState.entities,
    DepartmentState.currentDepartments
  ])
  public static activeInsights(
    state: CollatableEntityCollections<InsightDTO>,
    patient: EntityDictionary<string, BffPatient>,
    currentDepartments: DepartmentDTO[] | null
  ): InsightDTO[] {
    let patients = Object.values(patient)
    if (currentDepartments) {
      patients = patients.filter(
        patient =>
          patient.department &&
          currentDepartments
            .map(department => department.id)
            .includes(patient.department.id)
      )
    }
    const currentInsights: any[] = []
    Object.values(state.entities).forEach(insight => {
      if (insight.patient && insight.patient.id) {
        const patient = Object.values(patients).find(
          p => p.id === insight.patient?.id
        )
        if (patient) {
          currentInsights.push({
            ...insight,
            patient: {
              ...patient,
              avatar: patient.avatar
            }
          })
        }
      }
    })
    return currentInsights.filter(insight => !insight.insightEndTime)
  }

  @Selector()
  public static totalCount(
    state: CollatableEntityCollections<InsightDTO>
  ): number {
    return state.totalCount
  }

  public override ngxsOnInit() {
    this.storeEvents.logout$
      .pipe(
        tap(() => {
          this.reset()
          this.clearPatientInsightsTotalResult()
        })
      )
      .subscribe()
  }

  @DataAction()
  public loadPatientInsights(
    @Payload('patientId') patientId?: string,
    @Payload('needPatientsIds') needPatientsIds: boolean | undefined = false,
    @Payload('status') status?: string
  ) {
    this.ctx.patchState({
      isLoading: true
    })
    const freeTextFilter = this.getState().freeTextFilter
    const allPatientIds = needPatientsIds
      ? this.searchInsightPatientByNameAndRoom(freeTextFilter).map(p => p.id)
      : patientId
        ? [patientId]
        : []
    const currentPage = this.getState().page
    if (!needPatientsIds) {
      return this.loadCurrentPatientInsights(
        currentPage,
        freeTextFilter,
        patientId!,
        status
      )
    }
    if (!allPatientIds.length && freeTextFilter.length) {
      this.upsertMany([])
      this.ctx.patchState({ isLoading: false })
      this.setIsInitialLoading(false)
      return of([])
    }

    return this.backendService
      .getBrowsingInsights(
        currentPage,
        freeTextFilter,
        patientId,
        allPatientIds,
        status
      )
      .pipe(
        tap(res => {
          const insights = [...res.data]

          if (!insights.length && freeTextFilter.length) {
            this.upsertMany([])
          } else {
            this.upsertMany(orderBy(insights, 'insightStartTime', 'desc'))
          }

          this.ctx.patchState({ isLoading: false })
          this.ctx.patchState({ isInitialLoading: false })
          const page = res.metadata.page?.page as number
          const limit = res.metadata.page?.limit as number
          const totalResults = res.metadata.page?.totalResults as number
          if ((page + 1) * limit >= totalResults) {
            this.ctx.patchState({ hasAllLoaded: true })
          }

          if (!freeTextFilter && insights.length) {
            this.patchState({
              totalCount: res.metadata.page?.totalResults
            })
          }
        }),
        catchError(error => {
          this.ctx.patchState({ isLoading: false })
          this.ctx.patchState({ isInitialLoading: false })
          this.errorState.setError({
            ...error,
            title: 'Error',
            details: 'Error getting insights'
          })
          return EMPTY
        })
      )
  }

  @DataAction()
  loadCurrentPatientInsights(
    @Payload('page') page: number,
    @Payload('freeTextSearch') freeTextSearch: string,
    @Payload('patientId') patientId: string,
    @Payload('status') status?: string
  ) {
    this.setLoading(true)
    return this.backendService
      .getBrowsingInsights(page, freeTextSearch, patientId, undefined, status)
      .pipe(
        tap((res: InsightsBackend) => {
          const insights = Object.values(this.getState().entities)
          this.removeAll()
          if (!res.data?.length && freeTextSearch.length) {
            this.upsertMany([])
          } else {
            if (page === 0) {
              this.upsertMany(
                orderBy([...res.data], 'insightStartTime', 'desc')
              )
            } else {
              this.upsertMany(
                orderBy([...insights, ...res.data], 'insightStartTime', 'desc')
              )
            }
          }
          this.setLoading(false)
          if (!freeTextSearch) {
            this.ctx.patchState({
              totalCount: res.metadata.page?.totalResults
            })
          }
        }),
        catchError(error => {
          this.setLoading(false)
          return of(error)
        })
      )
  }

  searchInsightPatientByNameAndRoom(freeTextSearch: string): BffPatient[] {
    const currentDepartments = this.departmentState.snapshot.currentDepartments
    let patients = Object.values(this.patientState.entities)
    if (currentDepartments && currentDepartments.length) {
      patients = entitiesFilter(
        freeTextSearch,
        patients.filter(
          patient =>
            patient.department &&
            currentDepartments
              .map(department => department.id)
              .includes(patient.department.id)
        )
      )
    } else {
      patients = entitiesFilter(
        freeTextSearch,
        patients.filter(patient => patient.department)
      )
    }
    return patients
  }

  @DataAction()
  public getPatientInsightsTotalResult(
    @Payload('patientId') patientId?: string
  ) {
    const ids: string[] = patientId
      ? [patientId]
      : this.searchInsightPatientByNameAndRoom('').map(p => p.id)
    return combineLatest(
      this.backendService.getBrowsingCombineInsights(0, ids),
      this.backendService.getBrowsingInsights(0, '', undefined, ids, undefined)
    ).pipe(
      tap(([active, all]) => {
        this.insightsAllActiveTotalCount$.next({
          allTotalCount: (ids.length && all?.metadata?.page?.totalResults) || 0,
          activeTotalCount:
            (ids.length && active?.metadata?.page?.totalResults) || 0
        })
      })
    )
  }

  @DataAction()
  public getResidentInsightsTotalRawResult(
    @Payload('residentId') residentId: string
  ) {
    return combineLatest([
      this.backendService.getBrowsingInsights(
        0,
        '',
        undefined,
        [residentId],
        'ongoing'
      ),
      this.backendService.getBrowsingInsights(
        0,
        '',
        undefined,
        [residentId],
        'ended'
      )
    ]).pipe(
      tap(([ongoing, ended]) => {
        this.insightsAllActiveTotalCount$.next({
          activeTotalCount: ongoing?.metadata?.page?.totalResults || 0,
          allTotalCount:
            (ongoing?.metadata?.page?.totalResults || 0) +
            (ended?.metadata.page?.totalResults || 0)
        })
      })
    )
  }

  clearPatientInsightsTotalResult() {
    this.insightsAllActiveTotalCount$.next({
      allTotalCount: 0,
      activeTotalCount: 0
    })
  }

  @DataAction()
  public loadPatientCombineInsights(@Payload('patientId') patientId?: string) {
    this.ctx.patchState({
      isLoading: true
    })
    const freeTextFilter = this.getState().freeTextFilter
    const patients = this.searchInsightPatientByNameAndRoom(freeTextFilter)
    const ids = patientId ? [patientId] : patients.map(p => p.id)
    const pageToSearch = this.getState().page
    return this.backendService
      .getBrowsingCombineInsights(pageToSearch, ids)
      .pipe(
        tap(res => {
          const insights = [...res.data]
          if (!ids.length && freeTextFilter.length) {
            this.upsertMany([])
          } else {
            this.upsertMany(
              orderBy(
                insights,
                [
                  item => (item.insightStatus === 'ongoing' ? 0 : 1),
                  'insightStartTime'
                ],
                ['asc', 'desc']
              )
            )
          }
          this.ctx.patchState({ isLoading: false })
          this.ctx.patchState({ isInitialLoading: false })
          const page = res.metadata.page?.page as number
          const limit = res.metadata.page?.limit as number
          const totalResults = res.metadata.page?.totalResults as number
          if ((page + 1) * limit >= totalResults) {
            this.ctx.patchState({ hasAllLoaded: true })
          }

          if (!freeTextFilter) {
            this.patchState({
              totalCount: res.metadata.page?.totalResults
            })
          }
        }),
        catchError(error => {
          this.ctx.patchState({ isLoading: false })
          this.ctx.patchState({ isInitialLoading: false })
          this.errorState.setError({
            ...error,
            title: 'Error',
            details: 'Error getting insights'
          })
          return EMPTY
        })
      )
  }

  @DataAction()
  public loadPatientCombinedInsightsRecursively(
    @Payload('patientId') patientId?: string
  ) {
    this.ctx.patchState({
      isLoading: true
    })
    const freeTextFilter = this.getState().freeTextFilter
    const patients = this.searchInsightPatientByNameAndRoom(freeTextFilter)
    const ids = patientId ? [patientId] : patients.map(p => p.id)
    return this.backendService.getBrowsingCombinedInsightsRecursively(ids).pipe(
      tap(insights => {
        if (!ids.length && freeTextFilter.length) {
          this.upsertMany([])
        } else {
          this.upsertMany(
            orderBy(
              insights,
              [
                item => (item.insightStatus === 'ongoing' ? 0 : 1),
                'insightStartTime'
              ],
              ['asc', 'desc']
            )
          )
        }
        this.ctx.patchState({ isLoading: false })
        this.ctx.patchState({ isInitialLoading: false })
        this.ctx.patchState({ hasAllLoaded: true })

        if (!freeTextFilter) {
          this.patchState({
            totalCount: insights.length
          })
        }
      }),
      catchError(error => {
        this.ctx.patchState({ isLoading: false })
        this.ctx.patchState({ isInitialLoading: false })
        this.errorState.setError({
          ...error,
          title: 'Error',
          details: 'Error getting insights'
        })
        return EMPTY
      })
    )
  }

  @DataAction()
  public setLoading(@Payload('isLoading') isLoading: boolean) {
    this.ctx.patchState({ isLoading })
  }

  @DataAction()
  setIsInitialLoading(@Payload('isInitialLoading') isInitialLoading: boolean) {
    this.ctx.patchState({ isInitialLoading })
  }

  protected setPaginationSetting(): Observable<any> {
    return EMPTY
  }

  protected loadEntitiesFromBackend(
    ids: string[] | undefined
  ): Observable<void> {
    return EMPTY
  }
}
